import React, { useEffect, useState, useCallback, useRef, useMemo, startTransition } from 'react';
import { Row, Col, Breadcrumb, BreadcrumbItem, Modal, ModalHeader, ModalBody } from 'reactstrap';
import config from '../../config';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import KPISection from '../../components/dashboard/KPISection';
import BookingOverview from '../../components/dashboard/BookingOverview';
import TripsOverview from '../../components/dashboard/TripsOverview';
import FleetOverview from '../../components/dashboard/FleetOverview';
import ActivityFeed from '../../components/dashboard/ActivityFeed';
import SkeletonCard from '../../components/dashboard/SkeletonCard';
import MapSkeleton from '../../components/dashboard/MapSkeleton';
import ActiveTripsFeed from '../../components/dashboard/ActiveTripsFeed';
import EscalationsPanel from '../../components/dashboard/EscalationsPanel';
import ReportsAnalytics from '../../components/dashboard/ReportsAnalytics';
import CustomerOverview from '../../components/dashboard/CustomerOverview';
import DriverOverview from '../../components/dashboard/DriverOverview';
import VehicleOverview from '../../components/dashboard/VehicleOverview';
import RevenueMonitoring from '../../components/dashboard/RevenueMonitoring';
import ErrorBoundary from '../../components/dashboard/ErrorBoundary';
import InfoPanel from '../../components/dashboard/InfoPanel';
import CityNavigationCard from '../../components/dashboard/CityNavigationCard';
import CityExplorer from '../../components/dashboard/CityExplorer';
import s from './OperationsDashboard.module.scss';
import '../../styles/operations-dashboard-dark.css';
import '../../styles/operations-dashboard-light.css';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDateFilter } from '../../contexts/DateFilterContext';
import { useCity } from '../../contexts/CityContext';
import opsWebSocket from '../../services/opsWebSocket';
import { fetchDriverEscalationSnapshot } from '../../actions/driverIssues';

const LiveMap = React.lazy(() => import('../../components/dashboard/LiveMap'));

const REFRESH_INTERVAL_MS = 60000; // 60 seconds — single dashboard poll
const UNASSIGNED_PANEL_LIMIT = 100;

const OperationsDashboard = () => {
  const history = useHistory();
  const unlockedCountFromStore = useSelector(
    (s) => (s.unlockedBookings ? s.unlockedBookings.unlockedCount : null)
  );
  const { dateRange } = useDateFilter();
  const { selectedCities } = useCity();
  const startDate = dateRange?.startDate || '';
  const endDate = dateRange?.endDate || '';

  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);
  const dashboardFetchInFlightRef = useRef(false);
  const [summaryData, setSummaryData] = useState(null);
  const [opsData, setOpsData] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [escalationsData, setEscalationsData] = useState(null);
  const [reportsData, setReportsData] = useState(null);
  const [customersData, setCustomersData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [error, setError] = useState(null);
  const [driverIssuesCount, setDriverIssuesCount] = useState(null);
  const [registeredFleetInfo, setRegisteredFleetInfo] = useState({
    label: 'Registered Vehicles',
    count: 0,
  });

  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activePanel, setActivePanel] = useState(null);

  // "Last Updated" indicator state
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null); // timestamp of last successful fetch
  const [secondsAgo, setSecondsAgo] = useState(null);
  const tickRef = useRef(null);

  // Current/today unassigned count from WebSocket (same source as Ops Map); not persisted in DB
  const [currentUnassignedCount, setCurrentUnassignedCount] = useState(null);

  // Idle drivers count — same `idle_drivers` WebSocket events as Trip Escalations (shared opsWebSocket; no extra connection).
  const [idleDriversCount, setIdleDriversCount] = useState(0);

  // City Navigation highlights (passed down to LiveMap → OpsMapView for map markers + pan)
  const [mapHighlights, setMapHighlights] = useState([]); // [{lat, lng, label, pincode?, city?}]
  const [isCityExplorerOpen, setIsCityExplorerOpen] = useState(false);

  // Tick the "X seconds ago" counter every second
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (lastUpdatedAt == null) return;
    tickRef.current = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastUpdatedAt) / 1000));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [lastUpdatedAt]);

  const fetchDashboardData = useCallback(async () => {
    if (dashboardFetchInFlightRef.current) return;
    dashboardFetchInFlightRef.current = true;
    if (!initialLoadDone.current) {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const qString = params.toString() ? `?${params.toString()}` : '';

      const { data: d } = await axios.get(`${config.dispatchApiUrl}/dashboard/all${qString}`);
      if (!d?.success) {
        throw new Error('Invalid dashboard/all response');
      }

      setSummaryData(d.summary);
      setOpsData(d.operations);
      setEscalationsData(d.escalations);
      setReportsData(d.reports);
      setCustomersData(d.customers);
      setRevenueData(d.revenue);
      startTransition(() => {
        setActivityData(Array.isArray(d.activity) ? d.activity : []);
      });
      setLastUpdatedAt(Date.now());
      setSecondsAgo(0);
      setError(null);
    } catch (err) {
      console.error('Failed to load dashboard data', err);
      setError(`API Error: ${err.message || 'Network Error'} — Please check backend connections.`);
    } finally {
      dashboardFetchInFlightRef.current = false;
      initialLoadDone.current = true;
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchDashboardData();
      }
    }, REFRESH_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboardData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchDashboardData]);

  // One-time fetch for Register Driver data; no dashboard polling attached.
  useEffect(() => {
    let mounted = true;
    const fetchRegisteredFleetInfo = async () => {
      try {
        const res = await fetch(`${config.opsApiBase}/driver-registration/list?status=all`);
        const data = await res.json();
        if (!mounted || !res.ok || !data?.success || !Array.isArray(data.records)) return;

        const registeredRows = data.records.filter((row) => row?.is_registered);
        const hasVehicleMapping = registeredRows.some(
          (row) => row?.vehicle_name != null && String(row.vehicle_name).trim() !== ''
        );

        setRegisteredFleetInfo({
          label: hasVehicleMapping ? 'Registered Vehicles' : 'Registered Drivers',
          count: registeredRows.length,
        });
      } catch (_) {
        // Keep default 0 when registration service is unavailable.
      }
    };

    fetchRegisteredFleetInfo();
    return () => {
      mounted = false;
    };
  }, []);

  // Keep dashboard Driver Issues count aligned with Notifications page logic.
  useEffect(() => {
    let mounted = true;
    const syncDriverIssuesCount = async () => {
      try {
        const snapshot = await fetchDriverEscalationSnapshot({
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          cities: selectedCities,
        });
        if (mounted) setDriverIssuesCount(snapshot.reportedCount);
      } catch (_) {
        // Retain dashboard value if sync call fails.
      }
    };

    syncDriverIssuesCount();
    return () => {
      mounted = false;
    };
  }, [startDate, endDate, selectedCities]);

  // Shared WebSocket: current unassigned count (same stream as LiveMap / Sidebar)
  useEffect(() => {
    const fetchCurrentUnassignedCount = async () => {
      try {
        const params = new URLSearchParams({
          filter: 'unassigned',
          use_live: 'true',
          limit: String(UNASSIGNED_PANEL_LIMIT),
        });
        const res = await axios.get(
          `${config.dispatchApiUrl}/dashboard/panel-overview?${params.toString()}`
        );
        const count = Number.isFinite(Number(res?.data?.count))
          ? Number(res.data.count)
          : Array.isArray(res?.data?.data)
            ? res.data.data.length
            : null;
        if (Number.isFinite(count)) {
          setCurrentUnassignedCount(count);
        }
      } catch (_) {
        // Keep last known count on transient failures.
      }
    };

    fetchCurrentUnassignedCount();

    const onEscalation = (data) => {
      if (Array.isArray(data.unassigned)) {
        setCurrentUnassignedCount(
          data.unassigned.length
        );
      } else if (typeof data.count === 'number') {
        setCurrentUnassignedCount(data.count);
      }
    };
    const onClear = () => setCurrentUnassignedCount(0);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchCurrentUnassignedCount();
      }
    };
    const onIdleDrivers = (data) => {
      let drivers = Array.isArray(data?.drivers) ? data.drivers : [];
      // Filter out dismissed idle drivers (same logic as TripEscalations)
      try {
        const stored = localStorage.getItem('dismissedIdleKeys');
        if (stored) {
          const dismissed = new Set(JSON.parse(stored));
          drivers = drivers.filter(d => {
            const key = `${d.driver_id}:${d.last_trip_id || ''}`;
            return !dismissed.has(key);
          });
        }
      } catch {}
      setIdleDriversCount(drivers.length);
    };
    const unsubEsc = opsWebSocket.subscribe('escalation', onEscalation);
    const unsubClear = opsWebSocket.subscribe('clear_all', onClear);
    const unsubIdle = opsWebSocket.subscribe('idle_drivers', onIdleDrivers);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      unsubEsc();
      unsubClear();
      unsubIdle();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const todayStr = new Date().toISOString().split('T')[0];
  const unlockedCount = unlockedCountFromStore ?? 0;
  const isTodaySelected = (!startDate && !endDate) ||
    (endDate === todayStr && (!startDate || startDate === todayStr));

  // Unassigned = historical (from API) + current/today (from WebSocket) when date includes today
  const bookingsForOverview = useMemo(() => {
    const base = opsData?.bookings || {};
    const historicalUnassigned = base.unassigned_bookings ?? 0;
    const unassignedTotal = isTodaySelected
      ? historicalUnassigned + (currentUnassignedCount ?? 0)
      : historicalUnassigned;
    return {
      ...base,
      unassigned_bookings: unassignedTotal,
      unlocked_bookings: unlockedCount,
    };
  }, [opsData?.bookings, isTodaySelected, currentUnassignedCount, unlockedCount]);

  // Helper: human-readable "last updated" label
  const lastUpdatedLabel = () => {
    if (secondsAgo == null) return 'Loading…';
    if (secondsAgo < 5) return 'Just now';
    if (secondsAgo < 60) return `${secondsAgo}s ago`;
    const m = Math.floor(secondsAgo / 60);
    return `${m}m ago`;
  };

  return (
    <div
      className={`${isDarkMode ? 'operations-dark-dashboard' : 'operations-light-dashboard'} ${s.root}`}
    >
      {/* Header row with breadcrumb and last-updated indicator */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap" style={{ gap: '8px' }}>
        <div>
          <Breadcrumb>
            <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
            <BreadcrumbItem active>Logistics Operations Dashboard</BreadcrumbItem>
          </Breadcrumb>
          <h1 className="mb-0">Logistics Operations Dashboard</h1>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '12px' }}>
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            style={{
              background: isDarkMode ? 'rgba(255,255,255,0.1)' : '#fff',
              color: isDarkMode ? '#fff' : '#333',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid #ccc',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <i className={`fa ${isDarkMode ? 'fa-sun-o' : 'fa-moon-o'}`}></i>
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </button>
          
          <div
            className="d-flex align-items-center"
            style={{
              background: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f8f9fa',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #dee2e6',
              borderRadius: '8px',
              padding: '6px 14px',
              fontSize: '0.78rem',
              color: isDarkMode ? '#adb5bd' : '#495057',
              gap: '6px',
            }}
          >
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: secondsAgo != null && secondsAgo < 90 ? '#28a745' : '#ffc107' }} />
            <span>Last Updated: <strong style={{ color: isDarkMode ? '#fff' : '#000' }}>{lastUpdatedLabel()}</strong></span>
            <button
              onClick={fetchDashboardData}
              style={{ background: 'none', border: 'none', color: isDarkMode ? '#6c757d' : '#adb5bd', cursor: 'pointer', padding: '0 0 0 4px', lineHeight: 1 }}
              title="Refresh now"
            >
              ↺
            </button>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <DashboardLayout>
        <div className="ops-kpi-container">
          <ErrorBoundary label="KPI Cards">
            <KPISection
              loading={loading}
              data={summaryData}
              onOpenPanel={setActivePanel}
              isTodaySelected={isTodaySelected}
              dateRange={dateRange}
            />
          </ErrorBoundary>
        </div>

        <div className={s.dashboardGrid}>
          {/* LEFT COLUMN - Operations Monitoring */}
          <div className={s.leftColumn}>
            {/* City Navigation entry point — placed right after KPIs / before Booking Overview */}
            <ErrorBoundary label="City Navigation">
              <CityNavigationCard
                onClick={() => setIsCityExplorerOpen(true)}
                cityCount={null /* can be wired from explorer later */}
              />
            </ErrorBoundary>

            {loading
              ? <SkeletonCard height="300px" />
              : <ErrorBoundary label="Booking Overview"><BookingOverview
                  data={bookingsForOverview}
                  idleDriversCount={idleDriversCount}
                  onIdleDriversClick={() => history.push({ pathname: '/app/trips/escalations', state: { openIdleDrivers: true } })}
                  onFieldClick={(filter) =>
                    setActivePanel({
                      type: 'bookings',
                      filter,
                      startDate: dateRange?.startDate,
                      endDate: dateRange?.endDate,
                      useLive: isTodaySelected,
                    })
                  }
                /></ErrorBoundary>
            }
            {loading
              ? <SkeletonCard height="300px" />
              : <ErrorBoundary label="Trips Overview"><TripsOverview
                data={opsData?.trips}
                onFieldClick={(filter) =>
                  setActivePanel({
                    type: 'trips',
                    filter,
                    startDate: dateRange?.startDate,
                    endDate: dateRange?.endDate,
                  })
                }
              /></ErrorBoundary>

            }
            {loading
              ? <SkeletonCard height="300px" />
              : <ErrorBoundary label="Customer Overview"><CustomerOverview
                  data={customersData}
                  onFieldClick={(filter) =>
                    setActivePanel({
                      type: 'customers',
                      filter,
                      startDate: dateRange?.startDate,
                      endDate: dateRange?.endDate,
                    })
                  }
                /></ErrorBoundary>
            }
            {loading
              ? <SkeletonCard height="300px" />
              : <ErrorBoundary label="Fleet Monitoring"><FleetOverview
                  data={opsData?.fleet}
                  registeredMetric={registeredFleetInfo}
                  onFieldClick={(filter) => {
                    if (filter === 'registered_vehicles') {
                      history.push({
                        pathname: '/app/dispatch/vehicles',
                        search: '?tab=register-vehicle',
                        state: { activeTab: 'register-vehicle' },
                      });
                      return;
                    }
                    setActivePanel({ type: 'fleet', filter });
                  }}
                /></ErrorBoundary>
            }
          </div>

          {/* CENTER COLUMN - Operations Map & Active Trips */}
          <div className={s.centerColumn}>
            <ErrorBoundary label="Live Map" minHeight="420px">
              <div className="live-map-panel">
                <React.Suspense fallback={<MapSkeleton height="420px" />}>
                  <LiveMap
                    highlightLocations={mapHighlights}
                    onClearHighlights={() => setMapHighlights([])}
                  />
                </React.Suspense>
              </div>
            </ErrorBoundary>
            
            <ErrorBoundary label="Active Trips Feed">
              <ActiveTripsFeed />
            </ErrorBoundary>
          </div>

          {/* RIGHT COLUMN - Operations Insights */}
          <div className={s.rightColumn}>
            {loading
              ? <SkeletonCard height="220px" />
              : <ErrorBoundary label="Escalations & Issues"><EscalationsPanel data={{
                  ...(escalationsData || {}),
                  driver_issues: driverIssuesCount ?? escalationsData?.driver_issues ?? 0,
                }} loading={loading} onFieldClick={(filter) => setActivePanel({ type: 'escalations', filter, startDate: dateRange?.startDate, endDate: dateRange?.endDate })} /></ErrorBoundary>
            }
            {loading
              ? <SkeletonCard height="220px" />
              : <ErrorBoundary label="Reports & Analytics"><ReportsAnalytics data={reportsData} loading={loading} /></ErrorBoundary>
            }
            {loading
              ? <SkeletonCard height="240px" />
              : <ErrorBoundary label="Revenue Monitoring"><RevenueMonitoring
                  data={revenueData}
                  loading={loading}
                  onOpenPanel={(panel) =>
                    setActivePanel({
                      ...panel,
                      startDate: dateRange?.startDate,
                      endDate: dateRange?.endDate,
                    })
                  }
                /></ErrorBoundary>
            }
            {loading
              ? <SkeletonCard height="280px" />
              : <ErrorBoundary label="Recent Activity"><ActivityFeed activities={activityData} /></ErrorBoundary>
            }
          </div>
        </div>

      </DashboardLayout>

      {/* City Explorer Modal */}
      <Modal
        isOpen={isCityExplorerOpen}
        toggle={() => setIsCityExplorerOpen(false)}
        centered
        className={s.wideDialog}
      >
        <ModalHeader toggle={() => setIsCityExplorerOpen(false)}>
          🏙️ City Navigation Explorer
        </ModalHeader>
        <ModalBody style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px', overflow: 'hidden' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
            <ErrorBoundary label="City Explorer">
              <CityExplorer
                onMarkLocation={(loc) => {
                  if (Array.isArray(loc)) {
                    setMapHighlights(loc);
                  } else {
                    setMapHighlights([loc]);
                  }
                }}
                onClearHighlights={() => setMapHighlights([])}
                onClose={() => setIsCityExplorerOpen(false)}
              />
            </ErrorBoundary>
          </div>
        </ModalBody>
      </Modal>

      <InfoPanel
        type={activePanel}
        onClose={() => setActivePanel(null)}
        refreshTrigger={lastUpdatedAt}
      />
    </div>
  );
};

export default OperationsDashboard;
